// Relays react-oauth2-code-pkce's storage across window boundaries via postMessage,
// since third-party iframe storage partitioning can prevent the iframe and a popup
// it opened from seeing each other's localStorage writes directly.
const AUTH_RELAY_MESSAGE_TYPE = 'lula-support-auth-relay';

// Distinguishes our sign-in popup from unrelated popups (e.g. "Manage company")
export const AUTH_POPUP_WINDOW_NAME = 'lulaSupportAuthPopup';

const STORAGE_KEY_PREFIX = 'ROCP_';
const PKCE_CODE_VERIFIER_KEY = 'PKCE_code_verifier';

// Only the session itself crosses the window boundary. In particular never relay
// ROCP_loginInProgress: postLogin runs before the library clears it, and a stale
// copy makes the next load fail with "Bad authorization state".
export const RELAYED_STORAGE_KEYS = [
  'ROCP_token',
  'ROCP_tokenExpire',
  'ROCP_refreshToken',
  'ROCP_refreshTokenExpire',
  'ROCP_idToken',
];

// State that only exists while a login is under way in this window
const LOGIN_STATE_KEYS = [
  'ROCP_loginInProgress',
  'ROCP_auth_state',
  PKCE_CODE_VERIFIER_KEY,
];

// Matches the library's own 30 second "about to expire" buffer
const TOKEN_EXPIRY_BUFFER_SECONDS = 30;

type TAuthRelayPayload = Record<string, string>;

export function isAuthPopupWindow(): boolean {
  return window.name === AUTH_POPUP_WINDOW_NAME;
}

// The opener only needs the postMessage relay when it sits in a (storage
// partitioned) iframe. A top-level opener, e.g. the "Manage company" window,
// already shares this window's localStorage.
function isOpenerFramed(): boolean {
  try {
    return window.opener.top !== window.opener;
  } catch (e) {
    return true;
  }
}

export function relayAuthTokensToOpener(): void {
  if (!window.opener) return;

  const payload: TAuthRelayPayload = {};

  RELAYED_STORAGE_KEYS.forEach((key) => {
    const value = localStorage.getItem(key);

    if (value !== null) payload[key] = value;
  });

  window.opener.postMessage(
    { type: AUTH_RELAY_MESSAGE_TYPE, payload },
    window.location.origin
  );
}

export function isAuthRelayMessage(
  event: MessageEvent
): event is MessageEvent<{ type: string; payload: TAuthRelayPayload }> {
  return !!event.data && event.data.type === AUTH_RELAY_MESSAGE_TYPE;
}

export function applyAuthRelayPayload(payload: TAuthRelayPayload): void {
  // Set the values, then dispatch synthetic 'storage' events so the running
  // AuthProvider (which only reacts to storage events from *other* windows)
  // picks up the change in place, without a disruptive full page reload.
  Object.entries(payload)
    .filter(([key]) => RELAYED_STORAGE_KEYS.includes(key))
    .forEach(([key, value]) => {
      const oldValue = localStorage.getItem(key);

      localStorage.setItem(key, value);
      window.dispatchEvent(
        new StorageEvent('storage', {
          key,
          oldValue,
          newValue: value,
          storageArea: localStorage,
          url: window.location.href,
        })
      );
    });
}

// Removes the whole stored session, so the popup always starts a fresh login
export function clearStoredSession(): void {
  Object.keys(localStorage)
    .filter(
      (key) =>
        key.startsWith(STORAGE_KEY_PREFIX) || key === PKCE_CODE_VERIFIER_KEY
    )
    .forEach((key) => localStorage.removeItem(key));
}

// Removes a half-finished login. The Zendesk iframe never runs a login itself,
// so anything left here is stale and would show "Bad authorization state".
export function clearLoginState(): void {
  LOGIN_STATE_KEYS.forEach((key) => localStorage.removeItem(key));
}

// True when the stored access token is still valid, i.e. another widget sharing
// this storage has already renewed the session
export function hasFreshSharedToken(): boolean {
  try {
    const token = localStorage.getItem('ROCP_token');
    const tokenExpire = JSON.parse(
      localStorage.getItem('ROCP_tokenExpire') || '0'
    );
    const nowInSeconds = Date.now() / 1000;

    return (
      !!token &&
      JSON.parse(token) !== '' &&
      Number(tokenExpire) > nowInSeconds + TOKEN_EXPIRY_BUFFER_SECONDS
    );
  } catch (e) {
    return false;
  }
}

// Running as the sign-in popup, after a fresh login: hand the session to the
// window that opened us, drop our own copy, and close
export function handOverTokensAndClose(): void {
  if (window.opener && isOpenerFramed()) {
    relayAuthTokensToOpener();
    clearStoredSession();
  }
  window.close();
}
